import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import { useLanguage } from "../../contexts/LanguageContext";
import { GetAxios } from "../../helpers/axiosHelpers";
import { Star, Quote, ArrowRight } from "lucide-react";

import "swiper/css";
import "swiper/css/pagination";
import { ctaBg } from "../../assets";

// Base URL for static files (uploads) — strip /api suffix since static assets are served at root
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(/\/api\/?$/, "");

const StarRating = ({ rating = 5 }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${i < rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
      />
    ))}
  </div>
);

const Testimonial = ({ sectionData }) => {
  const { t, language } = useLanguage();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? t(key));
  const [apiItems, setApiItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    GetAxios("/testimonials/public")
      .then((res) => {
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          setApiItems(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const localeItems = t("testimonials.items");

  const testimonials = apiItems.length > 0
    ? apiItems.map((item) => ({
        name: item.nameEn,
        role: item.role,
        quote: (language === "ja" && item.quoteJa) ? item.quoteJa : item.quoteEn,
        photoUrl: item.photoUrl || null,
        rating: item.rating || 5,
      }))
    : Array.isArray(localeItems) ? localeItems : [];

  if (testimonials.length === 0) return null;

  const getPhotoSrc = (photoUrl) => {
    if (!photoUrl) return null;
    return photoUrl.startsWith("http") ? photoUrl : `${API_BASE}${photoUrl}`;
  };

  return (
    <>
      {/* Testimonial Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-[1280px] mx-auto px-8">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-14 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-accent-green rounded-full"></div>
                <span className="text-[10px] font-black text-accent-green uppercase tracking-[0.25em]">
                  {pick(sectionData?.labelEn, sectionData?.labelJa, "testimonials.sectionLabel")}
                </span>
              </div>
              <h2 className="text-[#0D1317] text-3xl md:text-4xl font-semibold tracking-tight capitalize">
                {pick(sectionData?.titleEn, sectionData?.titleJa, "testimonials.title")}
              </h2>
            </div>
            <p className="text-[#4A5D69] text-sm max-w-xs leading-relaxed">
              {pick(sectionData?.subtitleEn, sectionData?.subtitleJa, "testimonials.subtitle")}
            </p>
          </div>

          {/* Slider Section */}
          <Swiper
            modules={[Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            pagination={{ clickable: true }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            breakpoints={{
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pb-14"
          >
            {testimonials.map((item, index) => {
              const photoSrc = getPhotoSrc(item.photoUrl);
              const initials = item.name
                ? item.name.split(" ").filter(Boolean).map((n) => n[0]).slice(0, 2).join("")
                : "?";

              return (
                <SwiperSlide key={index} className="h-auto">
                  <div className="bg-white p-7 rounded-2xl shadow-sm border border-slate-100 h-full flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:border-slate-200 hover:-translate-y-1 group relative overflow-hidden">
                    {/* Decorative quote icon */}
                    <div className="absolute top-4 right-4 opacity-[0.06] group-hover:opacity-[0.1] transition-opacity">
                      <Quote className="w-16 h-16 text-subidha-blue" />
                    </div>

                    {/* Rating */}
                    <div className="mb-4">
                      <StarRating rating={item.rating || 5} />
                    </div>

                    {/* Quote */}
                    <p className="text-[#4A5D69] text-sm leading-relaxed mb-6 flex-1 relative z-10">
                      "{item.quote}"
                    </p>

                    {/* User Info */}
                    <div className="flex items-center gap-3.5 pt-5 border-t border-slate-100">
                      <div className="w-12 h-12 rounded-full border-2 border-accent-green/20 bg-subidha-blue flex items-center justify-center shrink-0 overflow-hidden shadow-md">
                        {photoSrc ? (
                          <img
                            src={photoSrc}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-white text-sm font-bold uppercase">{initials}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-[#063255] text-sm font-bold leading-tight">
                          {item.name}
                        </p>
                        <p className="text-[#367FAC] text-xs font-medium mt-0.5">
                          {item.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        <style jsx global>{`
          .swiper-pagination-bullet {
            background: #c7c7c7 !important;
            opacity: 1 !important;
            width: 8px !important;
            height: 8px !important;
            transition: all 0.3s ease !important;
          }
          .swiper-pagination-bullet-active {
            background: #367fac !important;
            width: 24px !important;
            border-radius: 4px !important;
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section className="py-14 bg-slate-50">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 bg-[#292F3A] rounded-[32px] overflow-hidden shadow-2xl">
            {/* Left Column: Photo */}
            <div className="relative h-56 lg:h-auto min-h-[360px]">
              <img
                src={ctaBg}
                alt="CTA background"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-blue-900/10 mix-blend-multiply"></div>
            </div>

            {/* Right Column: Message with numbered steps */}
            <div className="flex flex-col justify-center p-10 lg:p-16">
              <h2 className="text-white text-2xl md:text-[36px] font-semibold leading-tight mb-5 tracking-tight">
                {pick(sectionData?.ctaButtonEn, sectionData?.ctaButtonJa, "testimonials.cta.title")}
              </h2>
              <p className="text-slate-300 text-sm mb-8 leading-relaxed">
                {t("testimonials.cta.subtitle")}
              </p>

              {/* 3-step process */}
              <ol className="space-y-3 mb-8">
                {[
                  t("testimonials.cta.steps.step1"),
                  t("testimonials.cta.steps.step2"),
                  t("testimonials.cta.steps.step3"),
                ].map((text, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-accent-green/20 border border-accent-green text-accent-green text-[11px] font-black flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-slate-300 text-sm">{text}</span>
                  </li>
                ))}
              </ol>

              <div>
                <button className="bg-[#569C27] text-white font-bold text-base px-8 py-3.5 rounded-full flex items-center gap-3 hover:bg-[#66b32e] hover:shadow-lg hover:shadow-green-900/20 transition-all active:scale-95 uppercase tracking-wide">
                  {t("testimonials.cta.button")}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Testimonial;
